import { motion } from 'framer-motion';
import type { ModelData, ResultTab } from '@/types/models';
import { RevenueTab } from './tabs/RevenueTab';
import { ChartsTab } from './tabs/ChartsTab';
import { PnLTab } from './tabs/PnLTab';
import { CashFlowTab } from './tabs/CashFlowTab';
import { BalanceSheetTab } from './tabs/BalanceSheetTab';
import { MetricsTab } from './tabs/MetricsTab';
import { ValuationTab } from './tabs/ValuationTab';
import { ScenarioTab } from './tabs/ScenarioTab';

interface ResultsTabsProps {
  modelData: ModelData;
  modelType: string;
  tabs: ResultTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  onDownloadExcel: () => void;
  isDownloading: boolean;
}

export function ResultsTabs({
  modelData,
  modelType,
  tabs,
  activeTab,
  onTabChange,
  onDownloadExcel,
  isDownloading,
}: ResultsTabsProps) {
  const renderTabContent = () => {
    switch (activeTab) {
      case 'revenue':
        return <RevenueTab data={modelData} />;
      case 'charts':
        return <ChartsTab data={modelData} modelType={modelType} />;
      case 'pnl':
        return <PnLTab data={modelData} />;
      case 'cashflow':
        return <CashFlowTab data={modelData} />;
      case 'balance':
        return <BalanceSheetTab data={modelData} />;
      case 'metrics':
        return <MetricsTab data={modelData} />;
      case 'valuation':
        return <ValuationTab data={modelData} />;
      case 'scenario':
        return <ScenarioTab data={modelData} />;
      default:
        return <RevenueTab data={modelData} />;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Tabs Navigation */}
      <div className="border-b border-slate-200 bg-slate-50 px-4 overflow-x-auto">
        <div className="flex gap-1 py-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
              }`}
            >
              {tab.icon && <span className="mr-1.5">{tab.icon}</span>}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {/* Header with Export Button */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">
            {tabs.find((t) => t.id === activeTab)?.label || 'Results'}
          </h2>
          <motion.button
            onClick={onDownloadExcel}
            disabled={isDownloading}
            className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isDownloading ? (
              <>
                <svg
                  className="animate-spin w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </>
            ) : (
              <>
                📊 Export to Excel
              </>
            )}
          </motion.button>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {renderTabContent()}
        </motion.div>
      </div>
    </div>
  );
}
